import { useLocation } from "react-router-dom";
import { getSetupProblems } from "@utils/charitySetup";
import { Charity } from "reduxStore/services/api-v2/charities";
import { useGetGlobalSettingsQuery } from "reduxStore/services/api-v2/globalSettings";

import VersionSensitiveLink from "components/atoms/VersionSensitiveLink";

type Conditions = {
  warnings: {
    [key: string]: {
      condition: boolean,
      message: string | JSX.Element
    }
  },
  notifications: {
    [key: string]: {
      condition: boolean,
      message: string | JSX.Element
    }
  },
}

// Matches the error summary (ErrorBox) so links stay readable on the red background (WCAG AA)
const WARNING_LINK_CLASSES = "font-bold underline text-red-700 hover:no-underline";

export default function CharityAdminBanners ({ charity } : { charity: Charity }) {
  const { data: globalSettings } = useGetGlobalSettingsQuery();
  const setupProblems = getSetupProblems(charity, globalSettings);
  // The admin home shows the go-live checklist instead, so these banners would repeat it
  const onHomeWithChecklist = useLocation().pathname.endsWith("/charity-admin/home") && !charity.is_retailer;

  const conditions: Conditions = {
    warnings: {
      // One catch-all banner; the edit profile page lists exactly what is left to do
      setup: {
        condition: setupProblems.length > 0 && !onHomeWithChecklist,
        message: <div>
          Donors can&apos;t give to you yet. You have {setupProblems.length} {setupProblems.length === 1 ? "thing" : "things"} left to set up.{" "}
          <VersionSensitiveLink to="/adminpanel" className={WARNING_LINK_CLASSES}>Finish setting up</VersionSensitiveLink>
        </div>
      },
      suspended: {
        condition: !!charity.suspended,
        message: <div>
          <span>Your account has been suspended. If you belief that this has been done in error then please </span>
          <VersionSensitiveLink to="/contact" className={WARNING_LINK_CLASSES}>contact us</VersionSensitiveLink>
          <span> to discuss further</span>
        </div>
      },
    },
    notifications: {
      unverified: {
        condition: !charity.verified && !onHomeWithChecklist,
        message: <div>
          <span>Thank you, your application has been submitted and you will be notified once we have approved your account. Please</span>
          <VersionSensitiveLink to="/contact"> contact us </VersionSensitiveLink>
          <span>if you have any questions</span>
        </div>
      }
    }
  };

  const notifications = Object.values(conditions.notifications).filter(({ condition }) => condition );
  const warnings = Object.values(conditions.warnings).filter(({ condition }) => condition );

  if (notifications.length === 0 && warnings.length === 0) return null;

  const banners = <div>
    {
      notifications.map(({ message }, i) => (
        <div key={i} className="flex flex-row items-center justify-center border-b-2 bg-dpLightGreen p-4">
          <div className="flex flex-col items-start w-full max-w-xl">
            <div>{message}</div>
          </div>
        </div>
      ))
    }
    {
      warnings.map(({ message }, i) => (
        <div key={i} className="flex flex-row items-center justify-center border-b-2 border-red-500 bg-red-50 p-4">
          <div className="flex flex-col items-start w-full max-w-xl">
            <div>{message}</div>
          </div>
        </div>
      ))
    }
  </div>;
  return <>
    <div className="z-20 hidden sm:block absolute top-0 left-0 right-0 shadow-md">
      {banners}
    </div>
    {/* Make space for the absolute positioned div at the top */}
    <div className="rounded-lg border-2 sm:opacity-0 mb-2 sm:mb-0">
      {banners}
    </div>
  </>;
};